import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * One page of the school's board. A slide is a chalk announcement, a picture, a
 * video or a table out of a results sheet; the screens turn the pages.
 */
export interface BoardSlide {
  id: string;
  kind: 'chalk' | 'image' | 'video' | 'table';
  visible: boolean;
  seconds?: number;
  title?: string;
  body?: string;
  /** Which chalk drawing sits above a written announcement. */
  icon?: string;
  image?: string;
  video?: string;
  cols?: string[];
  grid?: string[][];
}

export const SLIDE_KIND_LABELS: Record<BoardSlide['kind'], string> = {
  chalk: 'إعلان بالطباشير',
  image: 'صورة',
  video: 'فيديو',
  table: 'جدول أو نتائج',
};

export const DEFAULT_SECONDS: Record<BoardSlide['kind'], number> = {
  chalk: 18,
  image: 18,
  video: 40,
  table: 30,
};

/** How long one page of a long table stays before the card turns to the next. */
const TABLE_PAGE_MS = 7000;

/*
 * The drawings are chalk on slate: one stroke width, open shapes, nothing
 * filled. Each is drawn rather than fetched so the board needs no pictures.
 */
export const CHALK_ICONS: Record<string, { label: string; path: JSX.Element }> = {
  none: { label: 'بلا رسم', path: <g /> },
  cap: {
    label: 'تخرّج',
    path: (
      <>
        <path d="M32 18 8 27l24 9 24-9-24-9Z" />
        <path d="M16 31v12c0 4 7 7 16 7s16-3 16-7V31" />
        <path d="M54 29v13M54 45a2.2 2.2 0 1 0 0 .1" />
      </>
    ),
  },
  star: { label: 'نجمة', path: <path d="M32 9l7 15 16 2-12 11 3 16-14-8-14 8 3-16L9 26l16-2 7-15Z" /> },
  trophy: {
    label: 'كأس',
    path: (
      <>
        <path d="M20 10h24v13c0 7-5 12-12 12s-12-5-12-12V10Z" />
        <path d="M20 14h-7c0 8 3 11 8 12M44 14h7c0 8-3 11-8 12" />
        <path d="M32 35v10M22 53h20l-2-8H24l-2 8Z" />
      </>
    ),
  },
  book: {
    label: 'كتاب',
    path: (
      <>
        <path d="M32 17C26 11 15 13 10 15v33c9-4 16-2 22 3 6-5 13-7 22-3V15c-5-2-16-4-22 2Z" />
        <path d="M32 17v34" />
      </>
    ),
  },
  bell: {
    label: 'جرس',
    path: (
      <>
        <path d="M46 42V28a14 14 0 1 0-28 0v14l-5 7h38l-5-7Z" />
        <path d="M27 49a5 5 0 0 0 10 0M32 10V6" />
      </>
    ),
  },
  calendar: {
    label: 'تقويم',
    path: (
      <>
        <rect x="9" y="13" width="46" height="42" rx="4" />
        <path d="M9 25h46M21 8v10M43 8v10M19 35h6M29 35h6M39 35h6M19 45h6M29 45h6" />
      </>
    ),
  },
  megaphone: {
    label: 'إعلان',
    path: (
      <>
        <path d="M12 26v12h9l22 11V15L21 26h-9Z" />
        <path d="M49 25c4 4 4 14 0 18M22 38v11h7V38" />
      </>
    ),
  },
  flag: {
    label: 'علم',
    path: (
      <>
        <path d="M17 8v48" />
        <path d="M17 12h32l-6 10 6 10H17" />
      </>
    ),
  },
};

export type ChalkIcon = keyof typeof CHALK_ICONS;

/** The chalk drawing above an announcement, with its laurels when it is the cap. */
export function ChalkArt({ name }: { name: string }): JSX.Element | null {
  const icon = CHALK_ICONS[name];
  if (!icon || name === 'none') return null;
  return (
    <svg className="bd-art" viewBox="0 0 64 64" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
        {icon.path}
      </g>
    </svg>
  );
}

/**
 * A table on the board: a results sheet is longer than any screen, so the slide
 * shows one screenful at a time and turns the page, the way a person would.
 */
function ChalkTable({ slide }: { slide: BoardSlide }): JSX.Element {
  const viewRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [layout, setLayout] = useState({ rowHeight: 0, perPage: 1, pages: 1 });

  const cols = slide.cols ?? [];
  const grid = slide.grid ?? [];

  useLayoutEffect(() => {
    const view = viewRef.current;
    const body = bodyRef.current;
    if (!view || !body) return;
    const measure = (): void => {
      const first = body.firstElementChild as HTMLElement | null;
      const rowHeight = first?.offsetHeight ?? 0;
      if (!rowHeight) return;
      const perPage = Math.max(1, Math.floor(view.clientHeight / rowHeight));
      setLayout({ rowHeight, perPage, pages: Math.max(1, Math.ceil(grid.length / perPage)) });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(view);
    return () => observer.disconnect();
  }, [grid.length, cols.length]);

  useEffect(() => {
    if (layout.pages < 2) {
      setPage(0);
      return;
    }
    const timer = setInterval(() => setPage((p) => (p + 1) % layout.pages), TABLE_PAGE_MS);
    return () => clearInterval(timer);
  }, [layout.pages]);

  const columns = `repeat(${Math.max(1, cols.length || grid[0]?.length || 1)}, minmax(0, 1fr))`;
  return (
    <div className="bd-table">
      {cols.length > 0 ? (
        <div className="bd-thead" style={{ gridTemplateColumns: columns }}>
          {cols.map((c, i) => (
            <span key={i}>{c}</span>
          ))}
        </div>
      ) : null}
      <div className="bd-tview" ref={viewRef}>
        <div className="bd-tbody" ref={bodyRef} style={{ transform: `translateY(-${page * layout.perPage * layout.rowHeight}px)` }}>
          {grid.map((row, i) => (
            <div className="bd-trow" key={i} style={{ gridTemplateColumns: columns }}>
              {row.map((cell, j) => (
                <span key={j}>{cell}</span>
              ))}
            </div>
          ))}
        </div>
      </div>
      {layout.pages > 1 ? (
        <div className="bd-tpages" aria-hidden="true">
          {Array.from({ length: layout.pages }, (_, i) => (
            <i key={i} className={i === page ? 'on' : ''} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** One slide, drawn on the slate. */
export function SlideView({ slide }: { slide: BoardSlide }): JSX.Element {
  if (slide.kind === 'image') {
    return slide.image ? (
      <figure className="bd-figure">
        <img src={slide.image} alt={slide.title ?? ''} />
        {slide.title ? <figcaption>{slide.title}</figcaption> : null}
      </figure>
    ) : (
      <div className="bd-chalk">
        <h2>لم تُختر صورة</h2>
      </div>
    );
  }

  if (slide.kind === 'video') {
    return slide.video ? (
      <figure className="bd-figure">
        {/* A hall screen has nobody to press play, and no speakers to spare. */}
        <video src={slide.video} autoPlay muted loop playsInline />
        {slide.title ? <figcaption>{slide.title}</figcaption> : null}
      </figure>
    ) : (
      <div className="bd-chalk">
        <h2>لم يُحدَّد رابط الفيديو</h2>
      </div>
    );
  }

  if (slide.kind === 'table') {
    return (
      <div className="bd-chalk bd-chalk-table">
        {slide.title ? <h2 className="bd-small-title">{slide.title}</h2> : null}
        <ChalkTable slide={slide} />
      </div>
    );
  }

  return (
    <div className="bd-chalk">
      <ChalkArt name={slide.icon ?? 'cap'} />
      <h2>{slide.title}</h2>
      <div className="bd-rule" />
      {slide.body ? <p>{slide.body}</p> : null}
    </div>
  );
}

/** Turn the pages: each slide holds the board for its own number of seconds. */
export function useSlideShow(slides: BoardSlide[]): number {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (index >= slides.length) setIndex(0);
  }, [slides.length, index]);
  useEffect(() => {
    if (slides.length < 2) return;
    const slide = slides[index % slides.length];
    const seconds = slide?.seconds ?? DEFAULT_SECONDS[slide?.kind ?? 'chalk'];
    const timer = setTimeout(() => setIndex((i) => (i + 1) % slides.length), seconds * 1000);
    return () => clearTimeout(timer);
  }, [index, slides]);
  return slides.length > 0 ? index % slides.length : 0;
}
